/*
validate_creditcard

usage:
validate_creditcard.cvc(anyInput)
validate_creditcard.cardnumber(anyInput)
validate_creditcard.cardowner(anyInput)
validate_creditcard.amount(anyInput)
validate_creditcard.day(anyInput)
validate_creditcard.month(anyInput)
validate_creditcard.year(anyInput)


return:
{
    'result': true / false,
    'msg': 'only 3 numbers'
}

*/
function containsOnlyDigits(t) {
	const nums = new RegExp(/^\+?[0-9]+$/);
	return nums.test(t);
}

function hasStringLength(t, n) {
	return (t && t.length && t.length === n);
}

function cardid(t) {
	return (containsOnlyDigits(t));
}

function cvc(t) {
	return (hasStringLength(t, 3) && containsOnlyDigits(t));
}

function splitIntoArrOfDigitsBelowTen(t) {
	// convert a number to an array with each number
	// input:1234123412341234
	// output:[1,2,3,4,1,2,3,4,1,2,3,4,1,2,3,4]
	const str = (t && t.toString ? t.toString() : null);
	const spl = (str ? str.split('') : null);
	const arr = [];
	const L = ((spl && spl.length) ? spl.length : 0);
	for (let i = 0; i < L; i += 1) {
		arr[i] = (Number(spl[i]) || 0);
	}
	return arr;
}

function luhnCheck(arr = []) {
	// argument shall be a number separated into an array: [1,2,3,4]
	// https://en.wikipedia.org/wiki/Luhn_algorithm
	// The formula verifies a number against its included check digit, which is usually appended
	// to a partial account number to generate the full account number. This number must pass the following test:
	// 1.From the rightmost digit, which is the check digit, and moving left, double the value of
	// every second digit. If the result of this doubling operation is greater than 9 (e.g., 8 × 2 = 16), then
	// either...
	// a) add the digits of the product (e.g., 16: 1 + 6 = 7, 18: 1 + 8 = 9)
	// or alternatively...
	// b) subtract 9 from the product (e.g., 16: 16 - 9 = 7, 18: 18 - 9 = 9).
	// 2. Take the sum of all the digits.
	// 3. If the total modulo 10 is equal to 0 (if the total ends in zero) then the number is valid according to the Luhn formula; else it is not valid.
	let L = arr.length;
	let isSecond = true;
	let cDigit = 0;
	let cSum = 0;
	while (L) {
		L -= 1;
		isSecond = !isSecond;
		cDigit = arr[L];
		if (isSecond) {
			cDigit *= 2;
			cDigit = (cDigit > 9 ? cDigit - 9 : cDigit);
		}
		cSum += cDigit;
	}
	return (cSum && cSum % 10 === 0);
}

const cardtypes = {
	Amex: {
		reg: '^3[47][0-9]{13}$',
		check: true
	},
	AustralianBankcard: {
		reg: '^5610-?[0-9]{4}-?[0-9]{4}-?[0-9]{4}$',
		check: true
	},
	BCGlobal: {
		reg: '^(6541|6556)[0-9]{12}$',
		check: true
	},
	CarteBlanche: {
		reg: '^389[0-9]{11}$',
		check: true
	},
	DinersClub: {
		reg: '^3(?:0[0-5]|[68][0-9])[0-9]{11}$',
		check: true
	},
	DiscoverCard: {
		reg: '^65[4-9][0-9]{13}|64[4-9][0-9]{13}|6011[0-9]{12}|(622(?:12[6-9]|1[3-9][0-9]|[2-8][0-9][0-9]|9[01][0-9]|92[0-5])[0-9]{10})$',
		check: true
	},
	InstaPayment: {
		reg: '^63[7-9][0-9]{13}$',
		check: true
	},
	JCB: {
		reg: '^(?:2131|1800|35[0-9]{3})[0-9]{11}$',
		check: true
	},
	KoreanLocal: {
		reg: '^9[0-9]{15}$',
		check: true
	},
	Laser: {
		reg: '^(6304|6706|6709|6771)[0-9]{12,15}$',
		check: true
	},
	Maestro: {
		reg: '^(?:5[0678][0-9][0-9]|6304|6390|67[0-9][0-9])[0-9]{8,15}$',
		check: true
	},
	Mastercard: {
		reg: '^5[1-5][0-9]{14}$',
		check: true
	},
	Solo: {
		reg: '^(6334|6767)[0-9]{12}|(6334|6767)[0-9]{14}|(6334|6767)[0-9]{15}$',
		check: true
	},
	Switch: {
		reg: '^(4903|4905|4911|4936|6333|6759)[0-9]{12}|(4903|4905|4911|4936|6333|6759)[0-9]{14}|(4903|4905|4911|4936|6333|6759)[0-9]{15}|564182[0-9]{10}|564182[0-9]{12}|564182[0-9]{13}|633110[0-9]{10}|633110[0-9]{12}|633110[0-9]{13}$', // eslint-disable-line max-len
		check: true
	},
	UnionPay: {
		reg: '^(62[0-9]{14,17})$',
		check: true
	},
	Visa: {
		reg: '^4[0-9]{12}(?:[0-9]{3})?$',
		check: true
	},
	VisaMaster: {
		reg: '^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14})$',
		check: true
	}
};

function checkForPatterns(cardtypesObj = {}, str = '') {
	/* loop through object, compare regexp to string, if the cardnumber validates to any of the specified patterns
	it is considered valid */
	const validations = [];
	let foundMatch = false;
	Object.keys(cardtypesObj).forEach((key) => {
		if (cardtypesObj[key].check && cardtypesObj[key].reg) {
			/* compare string to regexp, if not valid type key into console */
			const pattern = new RegExp(cardtypesObj[key].reg);
			const valid = pattern.test(str);
			const info = (valid ? key : false);
			foundMatch = (valid || foundMatch);
			validations.push(info);
		}
	});
	/*
	// debug-script to print matches to a method
	validations.forEach((elm) => {
		if (elm) {
			console.log('match:', str, elm);
		}
	});*/
	return Boolean(foundMatch);
}

function hasCreditcardFormat(t) {
	const str = (t && t.toString ? t.toString() : null);
	const validPattern = checkForPatterns(cardtypes, str);
	const valid = (str && validPattern);
	return Boolean(valid);
}

function cardnumber(t) {
	const possibleCard = hasCreditcardFormat(t);
	const splitValue = (possibleCard ? splitIntoArrOfDigitsBelowTen(t) : null);
	const validCard = (splitValue ? luhnCheck(splitValue) : false);
	return validCard;
}

function cardowner(t) {
	// TODO preliminary method of validation
	// desktopsite: customWordCount 2
	const twowords = new RegExp(/^[a-zåäöA-ZÅÄÖ]{1,20}\s[a-zåäöA-ZÅÄÖ]{1,20}$/);
	return twowords.test(t);
	// return (/^[a-zåäöA-ZÅÄÖ]{1,20}\s[a-zåäöA-ZÅÄÖ]{1,20}$/).test(t);
}

function month(t) {
	return (containsOnlyDigits(t) && (parseInt(t, 10) < 13));
}

function year(t) {
	return (containsOnlyDigits(t) && (parseInt(t, 10) > 2000) && (parseInt(t, 10) < 3000));
}

function noEarlierThan(m, y) {
	const valMonth = (month(m) ? parseInt(m, 10) : null);
	const valYear = (year(y) ? parseInt(y, 10) : null);
	let valid = false;
	const currentYear = new Date().getFullYear();
	const currentMonth = new Date().getMonth();
	if (valMonth && valYear) {
		if (valYear < currentYear) {
			valid = false;
		} else if (valYear === currentYear) {
			/* if the input year is this year, then the month must be minimum this month
			important:
			The months go from 0 - 11 and input might be 1-12*/
			valid = Boolean(valMonth > currentMonth);
		} else {
			valid = true;
		}
	}
	return valid;
}
export default {
	cardid(t) {
		return cardid(t);
	},
	cvc(t) {
		return cvc(t);
	},
	number(t) {
		return cardnumber(t);
	},
	owner(t) {
		return cardowner(t);
	},
	date(m, y) {
		return noEarlierThan(m, y);
	},
	month(t) {
		return month(t);
	},
	year(t) {
		return year(t);
	},
	__testHasCreditcardFormat(t) {
		return hasCreditcardFormat(t);
	},
	__testSplitIntoArrOfDigitsBelowTen(t) {
		return splitIntoArrOfDigitsBelowTen(t);
	},
	__testLuhnCheck(t) {
		const splitNum = splitIntoArrOfDigitsBelowTen(t);
		return luhnCheck(splitNum);
	}
};



// WEBPACK FOOTER //
// ./mobile-core/src/payment/payment/lib/validate-creditcard.js